// Derivations that keep the console truthful for a real tenant: every status
// shown here comes from the tenant's own data, or is neutral when there is none.
// The demo keeps its sample copy; callers only use these for real tenants unless
// noted otherwise.
import type {
  AuditEvent,
  CertificateAuthorityConnector,
  CertificateSet,
  CryptoPolicy,
  IntegrationRecord,
  PlatformPolicy,
  SignatureAlgorithm,
  TransactionRecord,
} from "@edgepki/protocol";
import { isScmId, missingScmConnectorIds } from "@edgepki/protocol";
import type { BreakGlassResumeRequest, BreakGlassState, CurrentUser, NodeRecord, Overview, ScimConnectorStatus } from "./api.js";
import { userDisplayName } from "./display.js";

export type StatusTone = "healthy" | "warning" | "offline" | "paused";

// ── Topbar & navigation ─────────────────────────────────────────────────────

export function topbarStatus(
  overview: Pick<Overview, "issuance_paused" | "trust_status">,
  nodes: ReadonlyArray<Pick<NodeRecord, "status">>,
): { value: StatusTone; label: string } {
  if (overview.issuance_paused || overview.trust_status === "issuance_paused") return { value: "paused", label: "issuance paused" };
  if (nodes.length === 0) return { value: "warning", label: "No Mesh Nodes connected" };
  const healthy = nodes.filter((node) => node.status === "healthy").length;
  if (healthy < nodes.length) return { value: "warning", label: `Degraded · ${healthy}/${nodes.length} nodes healthy` };
  if (overview.trust_status !== "healthy") return { value: "warning", label: "Degraded" };
  return { value: "healthy", label: "trust path healthy" };
}

function pendingResumeRequest(requests: ReadonlyArray<BreakGlassResumeRequest>, now: number): BreakGlassResumeRequest | undefined {
  return requests.find((request) => request.status === "pending" && Date.parse(request.expires_at) > now);
}

export function hasActiveIncident(
  overview: Pick<Overview, "issuance_paused">,
  breakGlass: Pick<BreakGlassState, "paused" | "requests"> | undefined,
  now = Date.now(),
): boolean {
  return overview.issuance_paused || Boolean(breakGlass?.paused) || Boolean(pendingResumeRequest(breakGlass?.requests ?? [], now));
}

// ── Profile ─────────────────────────────────────────────────────────────────

export function profileSummary(
  user: Pick<CurrentUser, "email" | "groups"> | undefined,
  demo: boolean,
): { initials: string; name: string; role?: string } {
  if (demo) return { initials: "AM", name: userDisplayName(user?.email), role: "Tenant administrator" };
  const name = userDisplayName(user?.email);
  const local = typeof user?.email === "string" ? (user.email.trim().split("@", 1)[0] ?? "") : "";
  const parts = local.split(/[^a-z0-9]+/i).filter(Boolean);
  const initials = (parts.length > 1 ? `${parts[0]![0]}${parts[1]![0]}` : (parts[0] ?? "").slice(0, 2)).toUpperCase();
  const group = user?.groups?.map((item) => item.trim()).find(Boolean);
  return group ? { initials, name, role: group } : { initials, name };
}

// ── Overview / trust health ─────────────────────────────────────────────────

export type PartyState = "ok" | "warn" | "neutral";

export function trustParties(overview: Pick<Overview, "issuance_paused" | "approval_quorum">): {
  sectigo: { title: string; state: PartyState };
  customer: { title: string; state: PartyState };
} {
  const quorum = overview.approval_quorum;
  const sectigo = overview.issuance_paused
    ? { title: "Issuance paused", state: "warn" as const }
    : { title: "Co-signs each transaction", state: "neutral" as const };
  const customer = quorum.eligible_nodes === 0
    ? { title: "No Mesh Node enrolled", state: "warn" as const }
    : quorum.available
      ? { title: `Quorum ready · ${quorum.healthy_nodes}/${quorum.required} healthy`, state: "ok" as const }
      : { title: `Quorum unavailable · ${quorum.healthy_nodes}/${quorum.required} healthy`, state: "warn" as const };
  return { sectigo, customer };
}

export function nodeHealthMetric(nodes: ReadonlyArray<Pick<NodeRecord, "status">>): {
  value: string; detail: string; tone: "default" | "good" | "warn";
} {
  const healthy = nodes.filter((node) => node.status === "healthy").length;
  if (nodes.length === 0) return { value: "0/0", detail: "No Mesh Nodes enrolled", tone: "warn" };
  return {
    value: `${healthy}/${nodes.length}`,
    detail: healthy === nodes.length ? "All enrolled nodes reporting healthy" : `${nodes.length - healthy} node${nodes.length - healthy === 1 ? "" : "s"} not healthy`,
    tone: healthy === nodes.length ? "good" : "warn",
  };
}

/** Active certificates that expire within `days` (and have not already expired or been revoked). */
export function expiringCertificates(sets: ReadonlyArray<Pick<CertificateSet, "active">>, now = Date.now(), days = 30): number {
  const horizon = now + days * 86_400_000;
  return sets.filter((set) => {
    if (!set.active || set.active.state === "revoked" || set.active.state === "retired") return false;
    const notAfter = Date.parse(set.active.not_after);
    return Number.isFinite(notAfter) && notAfter > now && notAfter <= horizon;
  }).length;
}

// ── Audit chain ─────────────────────────────────────────────────────────────

export type AuditVerification =
  | { state: "pending" }
  | { state: "empty" }
  | { state: "verified"; checked: number; from: number; to: number; fromGenesis: boolean }
  | { state: "mismatch"; sequence: number; reason: "hash" | "link" | "head" };

/**
 * Re-hashes the loaded audit window exactly as the edge does (canonical SHA-256
 * over the event without `event_hash`) and checks that each event links to its
 * predecessor and that the newest event is the reported chain head.
 */
export async function verifyAuditWindow(
  events: ReadonlyArray<AuditEvent>,
  head: Overview["audit_head"] | undefined,
  hash: (value: unknown) => Promise<string>,
): Promise<AuditVerification> {
  if (events.length === 0) return head && head.sequence > 0 ? { state: "mismatch", sequence: head.sequence, reason: "head" } : { state: "empty" };
  const ordered = [...events].sort((left, right) => left.sequence - right.sequence);
  for (let index = 0; index < ordered.length; index++) {
    const event = ordered[index]!;
    const { event_hash: eventHash, ...core } = event;
    let computed: string;
    try {
      computed = await hash(core);
    } catch {
      return { state: "mismatch", sequence: event.sequence, reason: "hash" };
    }
    if (computed !== eventHash) return { state: "mismatch", sequence: event.sequence, reason: "hash" };
    const previous = ordered[index - 1];
    if (previous && previous.sequence + 1 === event.sequence && event.previous_hash !== previous.event_hash) {
      return { state: "mismatch", sequence: event.sequence, reason: "link" };
    }
    if (previous && previous.sequence + 1 !== event.sequence) return { state: "mismatch", sequence: event.sequence, reason: "link" };
    if (event.sequence === 1 && event.previous_hash !== "genesis") return { state: "mismatch", sequence: 1, reason: "link" };
  }
  const newest = ordered[ordered.length - 1]!;
  if (head && (head.sequence !== newest.sequence || head.hash !== newest.event_hash)) {
    return { state: "mismatch", sequence: head.sequence, reason: "head" };
  }
  const oldest = ordered[0]!;
  return { state: "verified", checked: ordered.length, from: oldest.sequence, to: newest.sequence, fromGenesis: oldest.sequence === 1 };
}

export function auditIntegrityLabel(result: AuditVerification): { title: string; detail: string; tone: StatusTone } {
  switch (result.state) {
    case "pending":
      return { title: "Verifying audit chain…", detail: "Re-hashing the loaded events in this browser.", tone: "warning" };
    case "empty":
      return { title: "No audit events yet", detail: "The chain starts with the first recorded action.", tone: "warning" };
    case "verified":
      return result.fromGenesis
        ? { title: "Chain verified from genesis", detail: `${result.checked} events re-hashed in this browser.`, tone: "healthy" }
        : { title: "Recent events verified", detail: `Events #${result.from}–#${result.to} re-hashed in this browser; older events are covered by exported proofs.`, tone: "healthy" };
    case "mismatch":
      return {
        title: "Audit chain could not be verified",
        detail: result.reason === "head"
          ? "The loaded events do not end at the reported chain head. Refresh, then export a proof to investigate."
          : `Event #${result.sequence} does not match its recorded ${result.reason === "hash" ? "hash" : "predecessor link"}. Export a proof to investigate.`,
        tone: "offline",
      };
  }
}

// ── Issuance path ───────────────────────────────────────────────────────────

export interface IssuanceStep { label: string; state: "done" | "pending" | "failed"; at?: string }

/** The most recent real transaction's progress; no step is claimed without evidence. */
export function latestIssuancePath(
  transactions: ReadonlyArray<TransactionRecord>,
  sets: ReadonlyArray<Pick<CertificateSet, "active">> = [],
): { transactionId: string; steps: IssuanceStep[] } | undefined {
  const record = [...transactions].sort((left, right) => Date.parse(right.created_at) - Date.parse(left.created_at))[0];
  if (!record) return undefined;
  const steps: IssuanceStep[] = [{ label: "Request received", state: "done", at: record.created_at }];
  const customerDenied = record.customer_decision?.decision === "deny";
  const quorum = record.approval_quorum;
  const customerApproved = !customerDenied && record.customer_decision?.decision === "allow" && (!quorum || quorum.received >= quorum.required);
  steps.push({
    label: quorum ? `Enterprise approved (${Math.min(quorum.received, quorum.required)}/${quorum.required})` : "Enterprise approved",
    state: customerDenied ? "failed" : customerApproved ? "done" : "pending",
    ...(record.customer_decision?.evaluated_at ? { at: record.customer_decision.evaluated_at } : {}),
  });
  const sectigo = record.sectigo_decision;
  steps.push({
    label: "Sectigo approved",
    state: sectigo?.decision === "deny" ? "failed" : sectigo?.decision === "allow" ? "done" : "pending",
    ...(sectigo?.evaluated_at ? { at: sectigo.evaluated_at } : {}),
  });
  const signed = Boolean(record.certificate_serial || record.certificate_pem);
  steps.push({ label: "Certificate signed", state: signed ? "done" : record.status === "denied" || record.status === "expired" ? "failed" : "pending" });
  if (signed && record.certificate_serial) {
    const serial = record.certificate_serial.toLowerCase();
    const deployed = sets.find((set) => set.active?.serial_number.toLowerCase() === serial);
    if (deployed?.active) {
      steps.push({
        label: "Deployment healthy",
        state: deployed.active.health === "healthy" ? "done" : deployed.active.health === "unhealthy" ? "failed" : "pending",
        ...(deployed.active.deployed_at ? { at: deployed.active.deployed_at } : {}),
      });
    }
  }
  return { transactionId: record.transaction.transaction_id, steps };
}

// ── Certificates ────────────────────────────────────────────────────────────

export function formatAlgorithm(algorithm: SignatureAlgorithm | string): string {
  const named: Record<string, string> = {
    "ECDSA-P256-SHA256": "ECDSA P-256",
    "ECDSA-P384-SHA384": "ECDSA P-384",
    "RSA-PSS-3072-SHA384": "RSA-PSS 3072",
    "HYBRID-ECDSA-P384-ML-DSA-65": "Hybrid P-384 + ML-DSA-65",
  };
  return named[algorithm] ?? algorithm;
}

/** The signature algorithm recorded on the transaction that issued the set's active certificate. */
export function certificateAlgorithm(
  set: Pick<CertificateSet, "active">,
  transactions: ReadonlyArray<TransactionRecord>,
): string | undefined {
  const active = set.active;
  if (!active) return undefined;
  const serial = active.serial_number.toLowerCase();
  const record = transactions.find((item) =>
    (item.certificate_serial && item.certificate_serial.toLowerCase() === serial) ||
    item.transaction.transaction_id === active.certificate_id);
  if (!record) return undefined;
  const algorithm = record.sectigo_decision?.effective_algorithm ?? record.customer_decision?.effective_algorithm ?? record.transaction.request.requested_algorithm;
  return algorithm ? formatAlgorithm(algorithm) : undefined;
}

export function cliIssueExample(policy: Pick<CryptoPolicy, "workload_profiles"> | undefined): string {
  const profile = Object.values(policy?.workload_profiles ?? {}).find((item) => item.allowed_spiffe_prefixes?.length);
  const prefix = profile?.allowed_spiffe_prefixes[0];
  if (!profile || !prefix) return "sectigo-edge cert issue --profile <profile-id> --spiffe-id <spiffe-id>";
  const spiffeId = `${prefix}${prefix.endsWith("/") ? "" : "/"}my-service`;
  return `sectigo-edge cert issue --profile ${profile.id} --spiffe-id ${spiffeId}`;
}

// ── Policies ────────────────────────────────────────────────────────────────

export function policyActivationStatus(
  policy: Pick<CryptoPolicy, "status" | "not_before" | "expires_at">,
  now = Date.now(),
): { value: StatusTone; label: string } {
  if (policy.status === "draft") return { value: "warning", label: "draft" };
  if (policy.status === "retired") return { value: "offline", label: "retired" };
  if (Date.parse(policy.expires_at) <= now) return { value: "offline", label: "expired" };
  if (Date.parse(policy.not_before) > now) return { value: "warning", label: "not yet effective" };
  return { value: "healthy", label: "active" };
}

export function platformEnforcementStatus(
  platform: Pick<PlatformPolicy, "status" | "expires_at"> | undefined,
  platformSHA256: string | undefined,
  now = Date.now(),
): { value: StatusTone; label: string } {
  if (!platform || !platformSHA256) return { value: "warning", label: "platform policy unavailable" };
  if (platform.status !== "active") return { value: "warning", label: `platform policy ${platform.status}` };
  if (Date.parse(platform.expires_at) <= now) return { value: "offline", label: "platform policy expired" };
  return { value: "healthy", label: "independently enforced" };
}

/** Scopes for a new workload profile, copied from the tenant's existing profile rather than placeholders. */
export function newProfileScopes(policy: Pick<CryptoPolicy, "workload_profiles">): { spiffe: string[]; dns: string[] } {
  const existing = Object.values(policy.workload_profiles ?? {});
  const spiffe = existing.find((profile) => profile.allowed_spiffe_prefixes?.length)?.allowed_spiffe_prefixes ?? [];
  const dns = existing.find((profile) => profile.allowed_dns_suffixes?.length)?.allowed_dns_suffixes ?? [];
  return { spiffe: [...spiffe], dns: [...dns] };
}

// ── Access ──────────────────────────────────────────────────────────────────

export function identityConfiguration(
  user: Pick<CurrentUser, "authentication_method">,
  connector: Pick<ScimConnectorStatus, "status" | "active_users"> | undefined,
): Array<{ label: string; value: string }> {
  const method = user.authentication_method;
  const authentication = method === "password" ? "Sectigo Edge account (password + MFA)"
    : method === "sso" ? "Enterprise SSO"
    : method === "development" ? "Development identity"
    : "Not reported";
  const federation = method === "sso" ? "Federated through your identity provider"
    : method === "password" ? "Not federated"
    : "Not configured";
  const provisioning = connector?.status === "active"
    ? `SCIM connector active${typeof connector.active_users === "number" ? ` · ${connector.active_users} provisioned user${connector.active_users === 1 ? "" : "s"}` : ""}`
    : connector?.status === "revoked" ? "SCIM token revoked"
    : "Direct role assignments (SCIM not configured)";
  return [
    { label: "Authentication", value: authentication },
    { label: "Federation", value: federation },
    { label: "Provisioning", value: provisioning },
  ];
}

// ── Incidents ───────────────────────────────────────────────────────────────

export function resumeRequestState(request: Pick<BreakGlassResumeRequest, "status" | "expires_at">, now = Date.now()): { value: StatusTone; label: string } {
  if (request.status === "pending" && Date.parse(request.expires_at) <= now) return { value: "offline", label: "expired" };
  if (request.status === "pending") return { value: "warning", label: "1 of 2 approvals" };
  return { value: request.status === "approved" ? "healthy" : "offline", label: request.status };
}

// ── CAs & integrations ──────────────────────────────────────────────────────

export function caAccountLabel(ca: Pick<CertificateAuthorityConnector, "provider" | "scm_account_id">): string {
  if (ca.provider === "sectigo_scm") return ca.scm_account_id ? `Account ${ca.scm_account_id}` : "SCM account not recorded";
  return "Key protected behind remote signer";
}

export interface ScmIdDraft {
  organization: string;
  certificateType: string;
}

export interface ScmIds {
  scm_organization_id: number;
  scm_certificate_type_id: number;
}

/** Client-side mirror of the edge rule: both SCM IDs are positive whole numbers (SCM uses 32-bit IDs). */
export function parseScmIdDraft(draft: ScmIdDraft): { ids?: ScmIds; errors: { organization?: string; certificateType?: string } } {
  const parse = (value: string): number | undefined => {
    const trimmed = value.trim();
    if (!/^[0-9]{1,10}$/.test(trimmed)) return undefined;
    const parsed = Number(trimmed);
    return isScmId(parsed) ? parsed : undefined;
  };
  const organization = parse(draft.organization);
  const certificateType = parse(draft.certificateType);
  const errors: { organization?: string; certificateType?: string } = {};
  if (organization === undefined) errors.organization = "Enter the numeric SCM organization ID (a positive whole number).";
  if (certificateType === undefined) errors.certificateType = "Enter the numeric SCM certificate type ID (a positive whole number).";
  return organization !== undefined && certificateType !== undefined
    ? { ids: { scm_organization_id: organization, scm_certificate_type_id: certificateType }, errors }
    : { errors };
}

/** Card/manage label for an SCM connector's enrollment IDs; undefined for other providers. */
export function scmIdsLabel(
  ca: Pick<CertificateAuthorityConnector, "provider" | "scm_organization_id" | "scm_certificate_type_id" | "incomplete_fields">,
): { complete: boolean; label: string } | undefined {
  if (ca.provider !== "sectigo_scm") return undefined;
  const missing = new Set(ca.incomplete_fields ?? []);
  for (const field of missingScmConnectorIds(ca)) missing.add(field);
  if (missing.size === 0) return { complete: true, label: `Org ${ca.scm_organization_id} · Cert type ${ca.scm_certificate_type_id}` };
  if (missing.size === 2) return { complete: false, label: "Missing organization and certificate type IDs" };
  return { complete: false, label: missing.has("scm_organization_id") ? "Missing organization ID" : "Missing certificate type ID" };
}

const cloudIntegrationKinds = new Set<IntegrationRecord["kind"]>(["sectigo_scm"]);

export function integrationTargets(record: Pick<IntegrationRecord, "kind" | "enabled" | "node_ids">): string {
  if (!record.enabled) return "Not active";
  if (cloudIntegrationKinds.has(record.kind)) return "Cloud";
  const count = record.node_ids.length;
  if (count === 0) return "No node assigned";
  return `${count} target${count === 1 ? "" : "s"}`;
}

export function integrationTestMessage(record: Pick<IntegrationRecord, "kind" | "status" | "last_error_code">): string {
  if (cloudIntegrationKinds.has(record.kind)) {
    if (record.status === "healthy") return "Integration test passed.";
    return `Test recorded, but connectivity was not verified${record.last_error_code ? ` (${record.last_error_code.replaceAll("_", " ")})` : ""}.`;
  }
  return "Test queued. The Mesh Node will report the adapter result over its authenticated outbound channel.";
}
